import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { superAdminService } from '@/services/SuperAdminService'
import { superAdminKeys } from '@/hooks/queryKeys'
import type { UserDto, UserListDto } from '@lib/dto/user.dto'
import type { InviteByEmailInput } from '@lib/input/user.input'

/** All platform SUPER_ADMIN users. */
export function useSuperAdmins(): UseQueryResult<UserListDto> {
  return useQuery({
    queryKey: superAdminKeys.list(),
    queryFn: () => superAdminService.list(),
    staleTime: 30_000,
  })
}

function useSuperAdminsInvalidation(): () => void {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: superAdminKeys.lists() })
  }
}

export function useInviteSuperAdmin(): UseMutationResult<UserDto, Error, InviteByEmailInput> {
  const invalidate = useSuperAdminsInvalidation()
  return useMutation({
    mutationFn: (input: InviteByEmailInput) => superAdminService.invite(input),
    onSuccess: invalidate,
  })
}

/**
 * Withdraw an invitation nobody accepted, removing the super admin. Offered only for a
 * PENDING invite: deactivating one would leave a row for someone who never
 * arrived and keep hold of their email address, so a mistyped invite could never
 * be re-sent to the corrected one.
 */
export function useDeleteInvitedSuperAdmin(): UseMutationResult<void, Error, { userId: string }> {
  const invalidate = useSuperAdminsInvalidation()
  return useMutation({
    mutationFn: (vars: { userId: string }) => superAdminService.deletePending(vars.userId),
    onSuccess: invalidate,
  })
}

export function useDeactivateSuperAdmin(): UseMutationResult<UserDto, Error, { userId: string }> {
  const invalidate = useSuperAdminsInvalidation()
  return useMutation({
    mutationFn: (vars: { userId: string }) => superAdminService.deactivate(vars.userId),
    onSuccess: invalidate,
  })
}

export function useReactivateSuperAdmin(): UseMutationResult<UserDto, Error, { userId: string }> {
  const invalidate = useSuperAdminsInvalidation()
  return useMutation({
    mutationFn: (vars: { userId: string }) => superAdminService.reactivate(vars.userId),
    onSuccess: invalidate,
  })
}

export function useResendSuperAdminInvite(): UseMutationResult<void, Error, { userId: string }> {
  return useMutation({
    mutationFn: (vars: { userId: string }) => superAdminService.resendInvite(vars.userId),
  })
}
